import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, message, Spin, Typography } from "antd";
import { PlusOutlined, FileAddOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SectionApiService from "../../services/SectionApiService";
import CreateLessonModal from "components/ui/CreateLessonModal";
import CreateSectionModal from "components/ui/CreateSectionModal";
import { LessonSortable, SectionSortable } from "./Sortable";
import { useParams } from "react-router-dom";
import MyVideoPlayer from "./MyVideoPlayer";
import CoursesApiService from "services/CoursesApiService";
import DOMPurify from 'dompurify';
import { useAuth } from "../../contexts/AuthContext";
import { Role } from "../../constants/Role";

const { Title } = Typography;

const STORAGE_SECTION_KEY = (courseId) => `learningzone-${courseId}-staffmanager-section`;
const STORAGE_LESSON_KEY = (courseId) => `learningzone-${courseId}-staffmanager-lesson`;

export default function ManageLearnCourse() {
  // Auth context
  const { hasAnyRole } = useAuth();
  
  // Check permissions
  const isAdminDev = hasAnyRole([Role.ADMIN_DEV]);
  const isAdminBusiness = hasAnyRole([Role.ADMIN_BUSINESS]);
  const isStaffCourseManager = hasAnyRole([Role.STAFF_COURSE_MANAGER]);
  const isStaffCourseMentor = hasAnyRole([Role.STAFF_COURSE_MENTOR]);
  
  // Determine if user can perform CUD operations
  const canCreate = isAdminDev || isStaffCourseManager;
  const canUpdate = isAdminDev || isStaffCourseManager;
  const canDelete = isAdminDev || isStaffCourseManager;
  const canReorder = isAdminDev || isStaffCourseManager;
  
  const { id: courseId } = useParams();
  const [sections, setSections] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secureVideoUrl, setSecureVideoUrl] = useState("");
  const [courseTitle, setCourseTitle] = useState("");

  // Modal states
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showEditLessonModal, setShowEditLessonModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);

  // Data for modal actions
  const [creatingSectionId, setCreatingSectionId] = useState(null);
  const [creatingSectionName, setCreatingSectionName] = useState("");
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [lessonDetail, setLessonDetail] = useState(null);
  const [creatingLessonLoading, setCreatingLessonLoading] = useState(false);
  const [expandedSectionIds, setExpandedSectionIds] = useState([]);


  const sensors = useSensors(useSensor(PointerSensor));

  // Fetch sections and course title on mount
  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await SectionApiService.getSections(courseId);
      setSections(res);
     
    } catch (e) {
      message.error(e?.message || "Failed to load course sections");
    }
    setLoading(false);
    // eslint-disable-next-line
  }, []);

  const fetchCourseById = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CoursesApiService.getCourseById(courseId);
      setCourseTitle(res?.data?.course?.name);
    } catch (e) {
      message.error(e?.message || "Failed to load course title");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    fetchCourseById();
  }, [fetchCourseById]);

  // Thêm useEffect để lấy trạng thái section/lesson khi mount
  useEffect(() => {
    if (isStaffCourseManager && courseId) {
      const savedSection = localStorage.getItem(STORAGE_SECTION_KEY(courseId));
      if (savedSection) setExpandedSectionIds(JSON.parse(savedSection));
      const savedLesson = localStorage.getItem(STORAGE_LESSON_KEY(courseId));
      if (savedLesson) {
        try {
          const { sectionId, lessonId } = JSON.parse(savedLesson);
          if (sectionId && lessonId) {
            setSelectedLesson({ sectionId, lessonId });
            handleSelectLessonPersist(sectionId, lessonId);
          }
        } catch {}
      }
    }
  }, [isStaffCourseManager, courseId]);

  // Sau khi fetch xong sections, đồng bộ trạng thái lesson/section từ localStorage
  useEffect(() => {
    if (sections.length > 0 && isStaffCourseManager && courseId) {
      const savedLesson = localStorage.getItem(STORAGE_LESSON_KEY(courseId));
      if (savedLesson) {
        try {
          const { sectionId, lessonId } = JSON.parse(savedLesson);
          // Kiểm tra lesson có tồn tại không
          const section = sections.find(s => s.id === sectionId);
          const lesson = section?.lessons.find(l => l.id === lessonId);
          if (section && lesson) {
            setExpandedSectionIds(ids => [...new Set([...ids, sectionId])]);
            setSelectedLesson({ sectionId, lessonId });
            handleSelectLessonPersist(sectionId, lessonId);
            return;
          }
        } catch {}
      }
      // Nếu không có trạng thái cũ, mặc định mở đầu tiên
      if (sections[0]?.lessons[0]) {
        setExpandedSectionIds(ids => [...new Set([...ids, sections[0].id])]);
        setSelectedLesson({ sectionId: sections[0].id, lessonId: sections[0].lessons[0].id });
        handleSelectLessonPersist(sections[0].id, sections[0].lessons[0].id);
      }
    }
    // eslint-disable-next-line
  }, [sections, isStaffCourseManager, courseId]);

  // Khi user expand/collapse section, lưu vào localStorage
  const handleExpandSection = (sectionId, expanded) => {
    let newExpanded = [...expandedSectionIds];
    if (expanded) {
      if (!newExpanded.includes(sectionId)) newExpanded.push(sectionId);
    } else {
      newExpanded = newExpanded.filter((id) => id !== sectionId);
    }
    setExpandedSectionIds(newExpanded);
    if (isStaffCourseManager && courseId) {
      localStorage.setItem(STORAGE_SECTION_KEY(courseId), JSON.stringify(newExpanded));
    }
  };

  // Khi user chọn lesson, lưu vào localStorage
  const handleSelectLessonPersist = async (sectionId, lessonId) => {
    setSelectedLesson({ sectionId, lessonId });
    if (isStaffCourseManager && courseId) {
      localStorage.setItem(
        STORAGE_LESSON_KEY(courseId),
        JSON.stringify({ sectionId, lessonId })
      );
    }
    try {
      const data = await SectionApiService.getLessonDetail(lessonId);
      setLessonDetail(data);
      if (data?.type === "Video") {
        const res = await SectionApiService.getLessonVideoUrl(lessonId);
        setSecureVideoUrl(res?.data.signedUrl);
      } else {
        setSecureVideoUrl("");
      }
    } catch (e) {
      setLessonDetail(null);
      setSecureVideoUrl("");
      message.error(e?.message || "Failed to load lesson detail");
    }
  };

  // Section handlers
  const handleDeleteSection = (section) => {
    Modal.confirm({
      title: "Delete section?",
      content: (
        <>
          Are you sure you want to delete section <b>{section.title}</b> and all its lessons?
        </>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await SectionApiService.deleteSection(section.id);
          message.success("Section deleted!");
          fetchSections();
        } catch (e) {
          message.error(e?.response?.data?.message || "Failed to delete section!");
        }
      },
    });
  };

  // Create/Edit Section with error handling
  const handleCreateOrEditSection = async (data, form) => {
    try {
      if (!data.name) {
        form.setErrors({ name: ["Section name is required"] });
        throw new Error("Section name is required");
      }
      if (editingSection) {
        await SectionApiService.updateSection(editingSection.id, data.name, data.description);
        message.success("Section updated!");
      } else {
        await SectionApiService.createSection(courseId, data.name, data.description);
        message.success("Section created!");
      }
      setShowSectionModal(false);
      setEditingSection(null);
      fetchSections();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save section!");
    }
  };

  // Lesson handlers
  const handleCreateLesson = async (payload, form) => {
    setCreatingLessonLoading(true);
    try {
      if (!payload.name) {
        form.setErrors({ name: ["Lesson name is required"] });
        throw new Error("Lesson name is required");
      }
      if (payload.type === "video") {
        if (!payload.videoFile) {
          form.setErrors({ videoFile: ["Please upload a video file"] });
          throw new Error("Please upload a video file");
        }
        
        const formData = new FormData();
        formData.append("type", "Video");
        formData.append("name", payload.name);
        formData.append("description", payload.description);
        formData.append("isPreview", payload.isPreview);
        formData.append("video", payload.videoFile);
        await SectionApiService.createLesson(creatingSectionId, formData, "video");
        
      } else {
        await SectionApiService.createLesson(
          creatingSectionId,
          {
            type: "html",
            name: payload.name,
            description: payload.description,
            isPreview: payload.isPreview,
            htmlContent: DOMPurify.sanitize(payload.htmlContent),
          },
          "html"
        );
      }
      message.success("Lesson created!");
      setShowLessonModal(false);
      setCreatingSectionId(null);
      setCreatingSectionName("");
      fetchSections(); // keep expandedSectionIds 
    } catch (e) {
     
      message.error(e?.response?.data?.message || "Failed to create lesson!");
    }
    setCreatingLessonLoading(false);
  };

  // Edit Lesson
  const handleEditLessonSubmit = async (payload, form) => {
    if (!editingLesson?.id) {
      message.warning("No lesson selected for editing!");
      return;
    }
    try {
      if (!payload.name) {
        form.setFields([{ name: "name", errors: ["Lesson name is required"] }]);
        throw new Error("Lesson name is required");
      }
      if (payload.type === "video") {
        if (payload.videoFile) {
          // Update with new video file
          const formData = new FormData();
          formData.append("type", "Video");
          formData.append("name", payload.name);
          formData.append("description", payload.description);
          formData.append("video", payload.videoFile);
          await SectionApiService.updateLesson(editingLesson.id, formData, "video");
        } else {
          // Update video lesson without changing video file
          const formData = new FormData();
          formData.append("type", "Video");
          formData.append("name", payload.name);
          formData.append("description", payload.description);
          await SectionApiService.updateLesson(editingLesson.id, formData, "video");
        }
      } else {
        // HTML lesson
        await SectionApiService.updateLesson(
          editingLesson.id,
          {
            type: "html",
            name: payload.name,
            description: payload.description,
            htmlContent: DOMPurify.sanitize(payload.htmlContent),
          },
          "html"
        );
      }
      message.success("Lesson updated!");
      setShowEditLessonModal(false);
      setEditingLesson(null);
      fetchSections();
      // Refresh lesson detail if it's the currently selected lesson
      if (selectedLesson && selectedLesson.lessonId === editingLesson.id) {
        handleSelectLessonPersist(selectedLesson.sectionId, selectedLesson.lessonId);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Failed to update lesson!");
    }
  };

  const handleDeleteLesson = (lesson) => {
    Modal.confirm({
      title: "Delete lesson?",
      content: (
        <>
          Are you sure you want to delete lesson <b>{lesson.title}</b>?
        </>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await SectionApiService.deleteLesson(lesson.id);
          message.success("Lesson deleted!");
          fetchSections();
        } catch (e) {
          message.error(e?.response?.data?.message || "Failed to delete lesson!");
        }
      },
    });
  };

  // Drag-and-drop handler
  const handleDragEnd = async (event) => {
    // Check if user has permission to reorder
    if (!canReorder) {
      message.warning("You don't have permission to reorder content");
      return;
    }
    
    const { active, over } = event;
    if (!over) return;
    // Section sort
    if (active.data.current?.type === "section") {
      if (active.id === over.id) return;
      const oldIdx = sections.findIndex((s) => s.id === active.id);
      const newIdx = sections.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(sections, oldIdx, newIdx);
      setSections(reordered);
      const frontId = newIdx > 0 ? reordered[newIdx - 1].id : null;
      const behindId = newIdx < reordered.length - 1 ? reordered[newIdx + 1].id : null;
      try {
        await SectionApiService.reorderSection(courseId, active.id, frontId, behindId);
        message.success("Section reorder success!");
      } catch (e) {
        message.error(e?.response?.data?.message || "Section reorder failed!");
        fetchSections();
      }
    }
    // Lesson sort
    if (active.data.current?.type === "lesson") {
      const sectionId = active.data.current.sectionId;
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      const lessons = section.lessons || [];
      const oldIdx = lessons.findIndex((l) => l.id === active.id);
      const newIdx = lessons.findIndex((l) => l.id === over.id);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      const newLessons = arrayMove(lessons, oldIdx, newIdx);
      setSections(sections.map((s) => (s.id === sectionId ? { ...s, lessons: newLessons } : s)));
      const frontId = newIdx > 0 ? newLessons[newIdx - 1].id : null;
      const behindId = newIdx < newLessons.length - 1 ? newLessons[newIdx + 1].id : null;
      try {
        await SectionApiService.reorderLesson(courseId, sectionId, active.id, frontId, behindId);
        message.success("Lesson reorder success!");
      } catch (e) {
        message.error(e?.response?.data?.message || "Lesson reorder failed!");
        fetchSections();
      }
    }
  };

  // ===== Responsive Render =====
  return (
    <div style={styles.layout}>
      <div style={styles.left}>
        <Title level={4} style={{ marginBottom: 12, fontWeight: 700 }}>
          {courseTitle || "Course title"}
        </Title>

        {lessonDetail?.type === "Video" ? (
          <MyVideoPlayer 
            style={styles.video} 
            url={secureVideoUrl} 
            disableTracking={isStaffCourseMentor}
          />
        ) : lessonDetail?.type === "HTML" ? (
          <div
            style={{
              ...styles.video,
              background: "#f7f7f7",
              fontSize: 15,
              fontWeight: 400,
              overflowY: "auto",
              padding: 20,
              minHeight: 300,
              height: 600,
              wordBreak: "break-word",
            }}
            className="lesson-html-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                lessonDetail?.htmlContent || lessonDetail?.overview || "<i>No content</i>",
                {
                  ALLOWED_TAGS: [
                    'b', 'i', 'u', 'em', 'strong', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                    'ul', 'ol', 'li', 'br', 'span', 'div', 'pre', 'code', 'blockquote',
                    'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img','href','link','embed'
                  ],
                  ALLOWED_ATTR: [
                     'title', 'alt', 'src', 'class', 'style','iframe'
                  ]
                }
              )
            }}
          />
        ) : (
          <div
            style={{
              ...styles.video,
              background: "#f7f7f7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#bbb",
              fontSize: 40,
              fontWeight: 600,
            }}
          >
            No content
          </div>
        )}
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>
          {lessonDetail?.title || "Select a lesson..."}
        </div>
        <div className="mt-3" style={{ borderBottom: "1px solid #eee" }}>
          {["overview"].map((key) => (
            <button
              key={key}
              style={{
                ...styles.tab,
                ...styles.tabActive,
                color: "white",
              }}
            >
              Overview
            </button>
          ))}
        </div>
        <div style={{ padding: "15px 0 0 0", minHeight: 90, fontSize: 15 }}>
          <div style={{ 
            whiteSpace: "pre-line",
            wordWrap: "break-word",
            wordBreak: "break-word",
            width: "100%"
          }}>{lessonDetail?.overview || "No overview."}</div>
        </div>
      </div>
      <div style={styles.right}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: 'sticky', top: 0, background: '#fff', zIndex: 2, paddingBottom: 12, borderBottom: '1.5px solid #f0f0f0', marginBottom: 8 }}>
          <Title level={4} style={{ fontWeight: 700, marginBottom: 0, color: "#2d3748" }}>
            Course Content
          </Title>
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="add-btnw"
              onClick={() => {
                setEditingSection(null);
                setShowSectionModal(true);
              }}
            >
              Add section
            </Button>
          )}
        </div>
        <div style={{ marginTop: 0, flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: 16 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : (
            <DndContext sensors={canReorder ? sensors : []} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {sections.map((section) => (
                  <SectionSortable
                    onEdit={canUpdate ? (sec) => {
                      setEditingSection(sec);
                      setShowSectionModal(true);
                    } : undefined}
                    onDelete={canDelete ? handleDeleteSection : undefined}
                    key={section.id}
                    section={section}
                    expanded={expandedSectionIds.includes(section.id)}
                    onExpand={() => handleExpandSection(section.id, true)}
                    onCollapse={() => handleExpandSection(section.id, false)}
                  >
                    <SortableContext
                      items={section.lessons?.map((l) => l.id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ padding: 16, paddingTop: 10 }}>
                        {section.lessons?.map((lesson) => (
                          <LessonSortable
                            key={lesson.id}
                            lesson={{
                              ...lesson,
                              onSelect: () => handleSelectLessonPersist(section.id, lesson.id),
                            }}
                            sectionId={section.id}
                            onEdit={canUpdate ? async (lesson) => {
                              const detail = await SectionApiService.getLessonDetail(lesson.id);
                              setEditingLesson(detail);
                              setShowEditLessonModal(true);
                              setCreatingSectionName(section.title);
                            } : undefined}
                            onDelete={canDelete ? handleDeleteLesson : undefined}
                            isActive={selectedLesson && selectedLesson.lessonId === lesson.id}
                          />
                        ))}
                        {canCreate && (
                          <Button
                            type="dashed"
                            block
                            icon={<FileAddOutlined />}
                            style={{
                              borderRadius: 7,
                              fontWeight: 500,
                              marginTop: 8,
                              color: "#1677ff",
                              border: "1px dashed #a3c5e8",
                            }}
                            onClick={() => {
                              setShowLessonModal(true);
                              setCreatingSectionId(section.id);
                              setCreatingSectionName(section.title);
                              handleExpandSection(section.id, true); // Đảm bảo section mở khi bấm add lesson
                            }}
                          >
                            Add lesson
                          </Button>
                        )}
                      </div>
                    </SortableContext>
                  </SectionSortable>
                ))}
              </SortableContext>
            </DndContext>
          )}
          {/* Sticky Footer
          <div style={{
            position: 'sticky',
            // bottom: 0,
            background: '#fff',
            zIndex: 2,
            borderTop: '1.5px solid #f0f0f0',
            padding: '12px 0 0 0',
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            
          </div> */}
        </div>
      </div>
      <CreateLessonModal
        show={showLessonModal}
        onClose={() => setShowLessonModal(false)}
        onSubmit={handleCreateLesson}
        sectionName={creatingSectionName}
        loading={creatingLessonLoading}
      />
      <CreateLessonModal
        show={showEditLessonModal}
        onClose={() => {
          setShowEditLessonModal(false);
          setEditingLesson(null);
        }}
        onSubmit={handleEditLessonSubmit}
        sectionName={creatingSectionName}
        initialValues={editingLesson}
        isEdit
      />
      <CreateSectionModal
        show={showSectionModal}
        onClose={() => {
          setShowSectionModal(false);
          setEditingSection(null);
        }}
        isEdit={!!editingSection}
        initialValues={editingSection}
        onSubmit={handleCreateOrEditSection}
      />
    </div>
  );
}

// Responsive
const styles = {
  layout: {
    display: "flex",
    gap: 30,
    padding: "25px 20px 25px 20px",
    background: "#f6f7fa",
    height: "100vh",
    fontFamily: "Segoe UI, Arial, sans-serif",
    boxSizing: "border-box",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    flexWrap: "wrap", // Responsive
  },
  left: {
    flex: 1,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 10px #e3e3e6",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minWidth: 280,
    marginBottom: 18,
    // border: "1.5px solid #e0e0e0",
  },
  right: {
    width: 500,
    minWidth: 320,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px #e3e3e6, 0 1.5px 0 #e3e3e6",
    padding: 24,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: "0 0 500px",
    // border: "1.5px solid #e0e0e0",
    position: "sticky",
    top: 0,
  },
  video: {
    width: "100%",
    borderRadius: 10,
    marginBottom: 16,
    background: "#111",
    height: 600,
  },
};
